import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { Chip } from "@material-ui/core";

import { PATHS } from "../../constants/paths";
import { auditParts } from "../../utils/rulesApi";
import { useRuleStyles } from "./styles";

export const rulesPath = {
  groups: () => PATHS.RULES,
  sets: (groupId) => `${PATHS.RULES}/groups/${groupId}`,
  newSet: (groupId, copyFrom) =>
    `${PATHS.RULES}/groups/${groupId}/sets/new${
      copyFrom ? `?copyFrom=${encodeURIComponent(copyFrom)}` : ""
    }`,
  set: (setId) => `${PATHS.RULES}/sets/${setId}`,
};

/** ACTIVE / DEPRECATED pill. */
export const StatusChip = ({ status }) => {
  const classes = useRuleStyles();
  const deprecated = status === "DEPRECATED";
  return (
    <Chip
      size="small"
      label={deprecated ? "Deprecated" : "Active"}
      className={deprecated ? classes.chipDeprecated : classes.chipActive}
    />
  );
};

StatusChip.propTypes = { status: PropTypes.string };

/** "Updated" cell: date and time, the user on a second line; rows a migration seeded say so. */
export const AuditStamp = ({ audit }) => {
  const classes = useRuleStyles();
  const parts = auditParts(audit);
  if (!parts) {
    return (
      <span
        className={classes.muted}
        title="Seeded by a migration; not edited since"
      >
        Not edited
      </span>
    );
  }
  return (
    <span className={classes.stamp}>
      <span>{parts.at}</span>
      {parts.by && <span className={classes.muted}>{parts.by}</span>}
    </span>
  );
};

AuditStamp.propTypes = { audit: PropTypes.object };

/** `items`: `[{ label, to }]`; the last item is the current page and is not a link. */
export const Breadcrumbs = ({ items }) => {
  const classes = useRuleStyles();
  return (
    <nav aria-label="Breadcrumb">
      <ol className={classes.crumbs}>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`}>
              {last || !item.to ? (
                <span aria-current={last ? "page" : undefined}>
                  {item.label}
                </span>
              ) : (
                <RouterLink to={item.to}>{item.label}</RouterLink>
              )}
              {!last && <span aria-hidden="true"> › </span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string,
    }),
  ).isRequired,
};

/** Debounced free-text value: calls `onCommit(trimmed)` 350 ms after the last change. */
export const useDebouncedCommit = (value, onCommit, delay = 350) => {
  const callback = React.useRef(onCommit);
  callback.current = onCommit;
  React.useEffect(() => {
    const handle = setTimeout(() => callback.current(value.trim()), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
};

/**
 * Groups parked under a "do not use …" name are legacy placeholders. They are hidden from the
 * default (Active) listing and from copy targets until someone marks them Deprecated for real;
 * choosing the Deprecated or All status filter still shows them.
 */
export const isParkedGroup = (group) =>
  /^\s*do\s*not\s*use\b/i.test(group?.name || "");

export const sortDirection = (state, key) =>
  state.sort === key ? (state.isAscending ? "asc" : "desc") : false;
